import React, { useState, useRef } from 'react';
import { API_ENDPOINTS, API_CONFIG, getApiUrl, fetchCsrfToken } from '../config/api';
import './OCRModal.css';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

const Toast: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${toast.type}`}>
      <div className="toast-message">{toast.message}</div>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
};

interface OCRModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTextExtracted: (text: string) => void;
}

const OCRModal: React.FC<OCRModalProps> = ({ isOpen, onClose, onTextExtracted }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [ocrResult, setOcrResult] = useState<any>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const newToast: Toast = {
      id: Date.now().toString(),
      message,
      type
    };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-dismiss toast after 5 seconds
    setTimeout(() => {
      removeToast(newToast.id);
    }, 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        const errorMsg = 'Only PDF files are supported for OCR processing';
        setError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }
      
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        const errorMsg = 'File size exceeds 10MB limit';
        setError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        const errorMsg = 'Only PDF files are supported for OCR processing';
        setError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }
      
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        const errorMsg = 'File size exceeds 10MB limit';
        setError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleProcessOCR = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setProgress(0);
    setProgressMessage('Initializing OCR processing...');
    setError(null);

    try {
      // Get CSRF token
      const csrf = await fetchCsrfToken();
      
      // Create form data
      const formData = new FormData();
      formData.append('file', selectedFile);
      // REVAMP: request the OCR processing path on the stateless endpoint.
      formData.append('mode', 'ocr');
      if (csrf) formData.append('csrfmiddlewaretoken', csrf);

      // Simulate progress updates
      const progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev < 90) {
            setProgressMessage('Processing document with OCR service...');
            return prev + 10;
          }
          return prev;
        });
      }, 500);

      // Make API request
      const response = await fetch(getApiUrl(API_ENDPOINTS.OCR_DOCUMENT), {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          // REVAMP: public build — shared frontend-client header (no JWT).
          'X-Frontend-Client': API_CONFIG.FRONTEND_CLIENT_SECRET,
        },
      });

      clearInterval(progressTimer);
      setProgress(100);
      setProgressMessage('OCR processing complete!');

      if (response.ok) {
        const result = await response.json();
        setOcrResult(result);
        setExtractedText(result.extracted_text);
        setShowResult(true);
        
        if (result.is_fallback) {
          showToast('Using fallback OCR service. For full functionality, set up Google Cloud Document AI.', 'info');
        } else {
          showToast('Document processed successfully! 📄', 'success');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || 'Document processing failed';
        setError(errorMsg);
        showToast(errorMsg, 'error');
      }
    } catch (err) {
      console.error('OCR processing error:', err);
      const errorMsg = 'Failed to process document. Please try again.';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  const handleUseText = () => {
    if (extractedText) {
      // Copy text to clipboard
      navigator.clipboard.writeText(extractedText).then(() => {
        showToast('Text extracted and copied to clipboard! 📋', 'success');
      }).catch(() => {
        showToast('Text extracted! Please copy it manually.', 'info');
      });
      
      // Call the parent callback
      onTextExtracted(extractedText);
      onClose();
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedFile(null);
      setError(null);
      setExtractedText('');
      setShowResult(false);
      setOcrResult(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`ocr-modal-overlay ${isOpen ? 'show' : ''}`} onClick={handleClose}>
      <div className="ocr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ocr-modal-header">
          <h2 className="ocr-modal-title">📄 OCR Document Processing</h2>
          <button className="ocr-modal-close" onClick={handleClose} disabled={isProcessing}>
            ×
          </button>
        </div>

        <div className="ocr-modal-content">
          {!showResult ? (
            <>
              <div className="ocr-info">
                <p>Upload a PDF document to extract text from your documents.</p>
                <ul>
                  <li>✅ Supports handwritten and printed text</li>
                  <li>✅ Maximum file size: 10MB</li>
                  <li>✅ Maximum page count: 30 pages</li>
                  <li>✅ Limit: 10 OCR extractions per day</li>
                  <li>✅ Free to use • processed in this session only</li>
                  <li>✅ High accuracy OCR processing</li>
                </ul>
              </div>

              <div className="file-upload-area" onDrop={handleDrop} onDragOver={handleDragOver}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="file-input"
                  disabled={isProcessing}
                />
                <div className="upload-content">
                  <div className="upload-icon">📄</div>
                  <p className="upload-hint">Only PDF files up to 10MB are supported</p>
                </div>
              </div>

              {selectedFile && (
                <div className="file-info">
                  <div className="file-details">
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                  </div>
                  <button 
                    className="remove-file-btn" 
                    onClick={() => setSelectedFile(null)}
                    disabled={isProcessing}
                  >
                    Remove
                  </button>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}


              {isProcessing && (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${progress}%`, 
                        background: 'var(--primary-gradient)' 
                      }} 
                    />
                  </div>
                  <p className="progress-message">{progressMessage} ({progress}%)</p>
                </div>
              )}

              <div className="ocr-actions">
                <button
                  className="btn btn-primary process-btn"
                  onClick={handleProcessOCR}
                  disabled={!selectedFile || isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Extract Text with OCR'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="ocr-result">
              <div className="result-header">
                <h3>
                  {ocrResult?.is_fallback ? '⚠️ Fallback OCR Processing Complete' : '✅ OCR Processing Complete'}
                </h3>
                {ocrResult?.is_fallback && (
                  <div className="fallback-warning">
                    <span className="warning-icon">⚠️</span>
                    <span>Using fallback OCR service for processing.</span>
                  </div>
                )}
                <div className="result-stats">
                  <span>📄 {ocrResult?.page_count || 0} pages</span>
                  <span>📝 {ocrResult?.word_count || 0} words</span>
                </div>
              </div>

              <div className="extracted-text-container">
                <h4>Extracted Text:</h4>
                <div className="extracted-text">
                  {extractedText}
                </div>
              </div>

              <div className="result-actions">
                <button className="btn btn-primary" onClick={() => {
                  if (extractedText) {
                    navigator.clipboard.writeText(extractedText).then(() => {
                      showToast('Copied to clipboard! 📋', 'success');
                    }).catch(() => {
                      showToast('Text extracted! Please copy it manually.', 'info');
                    });
                    onTextExtracted(extractedText);
                  }
                }}>
                  Copy
                </button>
                <button className="btn btn-secondary" onClick={() => setShowResult(false)}>
                  Process Another File
                </button>
                <button className="btn btn-secondary" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

export default OCRModal; 